// src/components/exam-player/renderers/SentenceBuilderRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function SentenceBuilderRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const tokens = Array.isArray(body.tokens) ? body.tokens : [];
  const layout = body.layout || "top-bottom";

  const getLabel = (tk) =>
    (tk?.label?.fa ??
      tk?.fa ??
      tk?.label?.en ??
      tk?.en ??
      tk?.key ??
      "") || "";

  // مقدار اولیه جواب کاربر
  const computeInitial = () => {
    if (!initialAnswers) return [];
    if (Array.isArray(initialAnswers)) {
      return initialAnswers.map((k) => String(k));
    }
    if (Array.isArray(initialAnswers?.tokens)) {
      return initialAnswers.tokens.map((k) => String(k));
    }
    if (Array.isArray(initialAnswers?.sequence)) {
      return initialAnswers.sequence.map((k) => String(k));
    }
    return [];
  };

  const [sequence, setSequence] = useState(computeInitial);

  // وقتی خود آیتم عوض می‌شود، state رو ریست کن
  useEffect(() => {
    setSequence(computeInitial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id]);

  // هر بار sequence عوض شد، به بالا پاس بده (برای ذخیره در attempt)
  useEffect(() => {
    onAnswerChange?.(sequence);
  }, [sequence, onAnswerChange]);

  // آیتم‌های استفاده‌نشده (بانک کلمات)
  const remainingTokens = useMemo(() => {
    const usedSet = new Set(sequence.map(String));
    return tokens.filter((tk) => !usedSet.has(String(tk.key)));
  }, [tokens, sequence]);

  const handleAddToken = (key) => {
    setSequence((prev) => {
      const k = String(key);
      if (prev.includes(k)) return prev; // دوباره اضافه نکن
      return [...prev, k];
    });
  };

  const handleRemoveToken = (key) => {
    const k = String(key);
    setSequence((prev) => prev.filter((x) => x !== k));
  };

  const handleClear = () => {
    setSequence([]);
  };

  const stemHtml = item?.stem?.fa || item?.stem?.en || "";

  const answerSentence = useMemo(() => {
    if (!sequence.length) return "";
    const map = new Map(
      tokens.map((tk) => [String(tk.key), getLabel(tk)])
    );
    return sequence
      .map((k) => map.get(String(k)) || String(k))
      .join(" ");
  }, [sequence, tokens]);

  const bankBlock = (
    <div className="space-y-2">
      <div className="text-xs text-gray-600 mb-1">
        {t(
          "sentence_builder.bank_help",
          "برای ساخت جمله روی کلمات کلیک کنید"
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {remainingTokens.length === 0 && (
          <span className="text-xs text-gray-400">
            {t(
              "sentence_builder.no_more_tokens",
              "کلمه‌ٔ استفاده‌نشده‌ای باقی نمانده است"
            )}
          </span>
        )}
        {remainingTokens.map((tk, idx) => {
          const label = getLabel(tk) || String(tk.key || `t${idx + 1}`);
          return (
            <button
              type="button"
              key={tk.key || `t${idx}`}
              onClick={() => handleAddToken(tk.key)}
              className="px-3 py-1.5 rounded-full border text-xs md:text-sm bg-white text-gray-800 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const answerBlock = (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600">
          {t("sentence_builder.your_answer", "جملهٔ شما")}
        </span>
        {sequence.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] text-red-500 hover:underline"
          >
            {t("sentence_builder.clear", "حذف همه")}
          </button>
        )}
      </div>

      <div className="min-h-[40px] border rounded-lg px-3 py-2 bg-gray-50 flex flex-wrap gap-2 items-center">
        {sequence.length === 0 ? (
          <span className="text-xs text-gray-400">
            {t(
              "sentence_builder.answer_placeholder",
              "برای ساخت جمله روی کلمات بالا کلیک کنید"
            )}
          </span>
        ) : (
          sequence.map((key, idx) => {
            const tk = tokens.find((x) => String(x.key) === String(key));
            const label =
              (tk && getLabel(tk)) || String(key || `t${idx + 1}`);

            return (
              <button
                type="button"
                key={`${key}-${idx}`}
                onClick={() => handleRemoveToken(key)}
                className="px-2.5 py-1 rounded-full bg-indigo-600 text-white text-xs md:text-sm flex items-center gap-1"
              >
                <span>{label}</span>
                <span className="text-[9px] opacity-80">
                  {t("sentence_builder.remove_token", "×")}
                </span>
              </button>
            );
          })
        )}
      </div>

      {answerSentence && (
        <div className="text-[11px] md:text-xs text-gray-600">
          <span className="font-semibold">
            {t("sentence_builder.as_text", "به صورت متن:")}
          </span>{" "}
          {answerSentence}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`space-y-3 ${
        isRtl ? "text-right" : "text-left"
      } sentence-builder-renderer`}
    >
      {!hideStem && stemHtml && (
        <div
          className="prose prose-sm max-w-none mb-2"
          dir={isRtl ? "rtl" : "ltr"}
          dangerouslySetInnerHTML={{ __html: stemHtml }}
        />
      )}

      {layout === "left-right" || layout === "right-left" ? (
        <div
          className={`flex flex-col md:flex-row gap-4 ${
            layout === "right-left" ? "md:flex-row-reverse" : ""
          }`}
        >
          <div className="flex-1">{bankBlock}</div>
          <div className="flex-1">{answerBlock}</div>
        </div>
      ) : layout === "bottom-top" ? (
        <>
          {answerBlock}
          {bankBlock}
        </>
      ) : (
        // default: top-bottom
        <>
          {bankBlock}
          {answerBlock}
        </>
      )}
    </div>
  );
}
